import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { PortalLayout } from "../../components/portal/PortalLayout";
import { StatusBadge } from "../../components/StatusBadge";
import { OrderTimeline } from "../../components/OrderTimeline";
import { ChevronLeft } from "lucide-react";

export default function PortalOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: order, isLoading } = useQuery({ queryKey: ["trade-order", id], queryFn: async () => (await api.get(`/trade/orders/${id}`)).data });
  useEffect(() => { if (order) document.title = `${order.order_number} | Sambiva`; }, [order]);

  if (isLoading) return <PortalLayout title="Order"><div className="text-center py-16 text-sambiva-muted">Loading…</div></PortalLayout>;
  if (!order) return <PortalLayout title="Order"><div className="text-center py-16">Order not found.</div></PortalLayout>;

  return (
    <PortalLayout title={order.order_number}>
      <button onClick={() => navigate("/portal/orders")} className="flex items-center gap-1 text-sambiva-muted hover:text-sambiva-red mb-5 text-sm"><ChevronLeft size={16} /> All orders</button>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-sambiva-charcoal/5 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-xl text-sambiva-charcoal">Order Status</h3>
              <StatusBadge status={order.status} />
            </div>
            <OrderTimeline status={order.status} history={order.status_history} />
          </div>

          <div className="bg-white rounded-2xl border border-sambiva-charcoal/5 overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="bg-sambiva-cream text-sambiva-muted text-left">
                <tr><th className="px-4 py-3 font-semibold">Product</th><th className="px-4 py-3 font-semibold">SKU</th><th className="px-4 py-3 font-semibold">Case</th><th className="px-4 py-3 font-semibold">Price</th><th className="px-4 py-3 font-semibold">Qty</th><th className="px-4 py-3 font-semibold">Total</th></tr>
              </thead>
              <tbody>
                {order.items.map((i, idx) => (
                  <tr key={idx} className="border-t border-sambiva-charcoal/5">
                    <td className="px-4 py-3 font-medium text-sambiva-charcoal">{i.product_name}</td>
                    <td className="px-4 py-3 text-sambiva-muted">{i.sku}</td>
                    <td className="px-4 py-3">{i.case_size}</td>
                    <td className="px-4 py-3">€{i.unit_price.toFixed(2)}</td>
                    <td className="px-4 py-3">{i.quantity}</td>
                    <td className="px-4 py-3 font-semibold">€{i.line_total.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-sambiva-charcoal/5 p-6">
            <h3 className="font-serif text-lg text-sambiva-charcoal mb-4">Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-sambiva-muted">Order Date</span><span className="text-sambiva-charcoal">{new Date(order.created_at).toLocaleDateString()}</span></div>
              <div className="flex justify-between"><span className="text-sambiva-muted">Subtotal</span><span className="text-sambiva-charcoal">€{order.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-sambiva-muted">VAT</span><span className="text-sambiva-charcoal">€{order.vat.toFixed(2)}</span></div>
              <div className="flex justify-between pt-2 border-t border-sambiva-charcoal/8 text-base"><span className="font-semibold text-sambiva-charcoal">Total</span><span className="font-bold text-sambiva-red">€{order.total.toFixed(2)}</span></div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-sambiva-charcoal/5 p-6">
            <h3 className="font-serif text-lg text-sambiva-charcoal mb-4">History</h3>
            <div className="space-y-3">
              {order.status_history.map((h, i) => (
                <div key={i} className="flex items-start gap-3 text-sm">
                  <span className="w-2 h-2 rounded-full bg-sambiva-green mt-1.5 shrink-0" />
                  <div><p className="font-medium text-sambiva-charcoal">{h.status}</p><p className="text-xs text-sambiva-muted">{new Date(h.timestamp).toLocaleString()}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
